// Renders the practice: line header, verdict strip, scoresheet, results, progress and the login wall.
// Reads the `practice` and `progress` store slices; every action goes back through practice.js.

import { pointsLong } from "../domain/points.ts";
import { Chess } from "#vendor/chessjs/chess.js";
import { store, game } from "../state/index.ts";
import { live } from "../state/live.ts";
import * as api from "../services/api.ts";
import * as practice from "./practice.js";
import * as catalog from "../services/catalog.ts";
import * as board from "./board.js";
import * as storage from "../services/storage.ts";
import * as track from "../services/track.ts";
import * as toast from "./toast.js";
import * as lines from "./lines.js";
import * as config from "./config.js";
import { repertoireMark, verdictGlyph, verdictLabel } from "../domain/moveRating.ts";
import { opponentLine } from "../domain/opponentLabel.ts";
import { STUDY_URL } from "../domain/dashboardView.ts";
import { isWallReached } from "../domain/practiceRules.ts";
import { sanFromUci } from "../domain/linePosition.ts";
import * as suggested from "../services/suggestedLines.ts";

const el = {};
let closePanels = () => {};
let suggestedLines = null;   // lines taken from the user's own games; asked for when the picker opens
let suggestedRequest = null;
const expandedSuggestions = new Set();   // paths ("0", "0.2") of the suggestions showing their variations
const SUGGESTIONS_SHOWN = 6;
let shownSuggestions = SUGGESTIONS_SHOWN;
let catalogAutoState = null;             // last fold state set by the code, to tell a manual toggle apart
let catalogTouched = false;
let focusBeforeWall = null;   // what had the focus when the login wall took it
let pickerCatalogLines = [];
let pickerMyLines = [];

const escapeHtml = (text) => String(text ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const stars = (count) => "★".repeat(count) + "☆".repeat(3 - count);
const LEVEL_LABELS = { beginner: "Beginner friendly", club: "Club level", advanced: "Advanced" };

/**
 * Practices a line that came from the user's own games — a suggestion of the picker, a highlight of
 * the dashboard. Working out which line it is belongs to services/suggestedLines; this page is the
 * one that can also start it where it stands, without going anywhere.
 *
 * @param options.navigate  true from a page that is not the study: the study is opened on the line.
 */
export async function practiceSuggestion(suggestion, { navigate = false } = {}) {
    await suggested.practiceSuggestion(suggestion, navigate ? {} : { start: (line) => practice.startLine(line) });
}

/** "Restart" and "Practice again" share this: a visitor out of free practices gets the login wall instead. */
export function restartPractice() {
    guarded(() => practice.restart());
}

export function initPracticeView(callbacks = {}) {
    closePanels = callbacks.closePanels || closePanels;
    [
        "practiceLineName", "practiceLineMoves", "practiceLineIdea", "practiceLineMeta", "practiceLineNote", "practiceLineBest", "practiceCount",
        "anotherLineBtn", "createYourLineBtn", "chooseLineBtn", "analysisPanel", "moveBadge", "repertoireBadge", "linePickerCatalog", "linePickerMyLines", "linePickerMyLinesList",
        "loginWall", "loginWallLead", "loginWallKeeps", "linePickerSuggested", "linePickerSuggestedList",
        "linePickerCatalogGroup", "linePickerMoreBtn", "resumePracticeBtn",
    ].forEach((id) => { el[id] = document.getElementById(id); });

    el.anotherLineBtn?.addEventListener("click", () => guarded(() => practice.startAnotherLine()));
    el.resumePracticeBtn?.addEventListener("click", () => practice.resume());
    store.subscribe('practice.resumable', renderResumeButton);
    renderResumeButton();
    // A visitor has no lines of their own to choose from, and creating one is what needs the account
    el.createYourLineBtn?.addEventListener("click", () => openLoginWall());
    store.subscribe('user.currentUser', renderLineButtons);
    renderLineButtons();
    el.linePickerMoreBtn?.addEventListener("click", () => { shownSuggestions = Infinity; renderSuggestedLines(); });
    // Folding the catalog is only a default: once it is opened or closed by hand it stays that way
    el.linePickerCatalogGroup?.addEventListener("toggle", () => {
        if (el.linePickerCatalogGroup.open !== catalogAutoState) catalogTouched = true;
    });
    // One delegated listener: the lists are re-rendered often (and asynchronously), and per-item
    // listeners could end up attached twice, starting or creating a line twice on a single click
    document.getElementById("linePickerDropdown")?.addEventListener("click", (e) => {
        const expand = e.target.closest("[data-expand]");
        if (expand) {
            toggleSuggestion(expand.dataset.expand);
            return;
        }
        const create = e.target.closest("[data-create]");
        if (create) {
            guarded(() => suggested.adoptSuggestion(suggestionAt(create.dataset.create),
                (line) => practice.startLine(line)));
            return;
        }
        const item = e.target.closest("[data-line]");
        if (item) guarded(() => chooseLine(item.dataset.line));
    });

    if (el.analysisPanel) {
        el.analysisPanel.open = storage.loadAnalysisOpen();
        el.analysisPanel.addEventListener("toggle", () => {
            storage.saveAnalysisOpen(el.analysisPanel.open);
            requestAnimationFrame(() => board.redrawBoard());
        });
    }

    el.loginWall?.querySelectorAll("[data-modal-close]").forEach((node) => node.addEventListener("click", closeLoginWall));
    el.loginWall?.querySelectorAll("a").forEach((link) => link.addEventListener("click", () => {
        const line = store.get('practice.line');
        if (line?.slug) storage.saveResume(line.slug);
        track.event("wall_click", { target: link.id });
    }));
    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && el.loginWall && !el.loginWall.classList.contains("hidden")) closeLoginWall();
    });
    document.addEventListener("keydown", handleShortcut);
    document.addEventListener("keydown", trapFocusInWall);

    store.subscribe('practice.line', renderHeader);
    store.subscribe('practice.status', renderAll);
    store.subscribe('practice.revision', renderAll);
    store.subscribe('practice.result', renderAll);
    store.subscribe('progress.summary', () => { renderHeader(); renderLinePicker(); });
    store.subscribe('progress.imported', announceImport);
    store.subscribe('practice.notice', announceNotice);
    store.subscribe('user.currentUser', renderLinePicker);
    window.addEventListener("resize", () => requestAnimationFrame(renderMoveBadge));

    renderAll();
}

/** Shortcuts for those who repeat a lot: Enter = again, N = another line, H = hint, T = take back. */
function handleShortcut(e) {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (["INPUT", "TEXTAREA", "SELECT", "BUTTON", "A"].includes(e.target.tagName)) return;
    if (document.querySelector(".config-dropdown.open") || !el.loginWall?.classList.contains("hidden")) return;
    const status = store.get('practice.status');
    const key = e.key.toLowerCase();
    if (key === "enter" && status === "finished") {
        e.preventDefault();
        guarded(() => practice.restart());
    } else if (key === "n" && ["ready", "finished"].includes(status)) {
        guarded(() => practice.startAnotherLine());
    } else if (key === "h" && ["ready", "playing"].includes(status)) {
        practice.hint();
    } else if (key === "t" && ["playing", "offbook"].includes(status)) {
        practice.takeBack();
    }
}

/** Runs an action that starts a practice, or shows the login wall when a visitor has used the free ones. */
/** "Choose line" needs lines to choose from; a visitor is offered the account that gives them instead. */
function renderLineButtons() {
    const loggedIn = !!store.get('user.currentUser');
    el.chooseLineBtn?.classList.toggle("hidden", !loggedIn);
    el.createYourLineBtn?.classList.toggle("hidden", loggedIn);
}

/**
 * "Back to <line>", when this tab has a practice of the user's waiting and the board is showing
 * something else. Coming back is always their click, never the page's decision.
 */
function renderResumeButton() {
    if (!el.resumePracticeBtn) return;
    const waiting = store.get('practice.resumable');
    el.resumePracticeBtn.classList.toggle("hidden", !waiting);
    if (!waiting) return;
    el.resumePracticeBtn.textContent = `Back to ${waiting.name}`;
    el.resumePracticeBtn.title = waiting.ownMoves
        ? `Continue the practice you left, ${waiting.ownMoves} move${waiting.ownMoves === 1 ? "" : "s"} in`
        : "Continue the practice you left";
}

export function guarded(action) {
    const summary = store.get('progress.summary');
    const reached = isWallReached({
        isLoggedIn: !!store.get('user.currentUser'),
        completedCount: summary ? summary.completedCount : 0,
        limit: store.get('progress.anonymousLimit'),
    });
    if (reached) {
        openLoginWall();
        return;
    }
    closePanels();
    action();
}

function renderAll() {
    renderHeader();
    renderMoveBadge();
}

// ---------------------------------------------------------------------------------------------
// Line header
// ---------------------------------------------------------------------------------------------

function renderHeader() {
    const line = store.get('practice.line');
    if (!line || !el.practiceLineName) return;
    el.practiceLineName.textContent = line.name;
    // A catalog line carries its moves in notation; a line of the user's only has them in UCI
    const moves = line.moves || (line.uci ? sanFromUci(line.uci).join(" ") : "");
    el.practiceLineMoves.textContent = moves ? catalog.numberedMoves(moves) : "";
    el.practiceLineIdea.textContent = line.idea || "";

    // The running count of practices: everything a visitor does is kept, and they should see it is
    const done = store.get('progress.summary')?.completedCount || 0;
    const finished = store.get('practice.status') === "finished" && store.get('practice.result')?.counted;
    el.practiceCount.textContent = done === 0 ? "Your first practice"
        : finished ? `${done} practice${done === 1 ? "" : "s"} completed`
            : `Practice ${done + 1}`;

    // Whoever answers on this line is who the header names: the catalog's own book, or the level
    // /settings asks Lichess for. They are rarely the same, and claiming one for the other was a lie.
    // A catalog line answered live (an account with Lichess) has the opponent of /settings, like any other
    const fromTree = line.source === "catalog" && !!practice.getTree();
    const opponent = opponentLine({
        source: fromTree ? "catalog" : "live", color: line.color,
        band: fromTree ? catalog.bandOf() : null,
        config: config.collectSparringConfig(),
    });
    el.practiceLineMeta.textContent = opponent.text;
    if (el.practiceLineNote) {
        el.practiceLineNote.textContent = opponent.note || "";
        el.practiceLineNote.title = opponent.noteTitle || "";
        el.practiceLineNote.classList.toggle("hidden", !opponent.note);
    }

    const record = store.get('progress.summary')?.lines?.[line.source === "catalog" ? line.slug : `line-${line.id}`];
    el.practiceLineBest.innerHTML = record && record.bestAccuracy !== null
        ? `<span class="practice-header__stars" title="Best accuracy on this line">${stars(record.stars)}</span>
           <span class="practice-header__best-text">Best ${Math.round(record.bestAccuracy)}% in ${record.plays} practice${record.plays === 1 ? "" : "s"}</span>`
        : `<span class="practice-header__best-text">${escapeHtml(LEVEL_LABELS[line.level] || "First time on this line")}</span>`;
}

// ---------------------------------------------------------------------------------------------
// Verdict strip
// ---------------------------------------------------------------------------------------------

function lastUserMove() {
    const moves = store.get('practice.moves') || [];
    return [...moves].reverse().find((m) => m && m.actor === "user") || null;
}

// The verdict strip is a component now: components/practice/VerdictStrip.tsx, and the rules it
// renders are in domain/verdictView.ts, where they finally have tests.

// ---------------------------------------------------------------------------------------------
// Scoresheet
// ---------------------------------------------------------------------------------------------

// ---------------------------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------------------------

// The score, the tally and the result are components now: components/practice/Scoresheet.tsx
// and PracticeResult.tsx, over domain/scoresheetView.ts and domain/resultView.ts.

/**
 * The two marks on the square the last move landed on: what the engine thought of it, and whether it
 * was the move your repertoire says you play. They sit in opposite corners and never replace each
 * other — "good move" and "not what you prepared" are both worth knowing, and they are often both true.
 */
function renderMoveBadge() {
    if (!el.moveBadge) return;
    const move = lastUserMove();
    const boardEl = document.getElementById("board");
    const status = store.get('practice.status');
    const moves = store.get('practice.moves') || [];
    const reply = move ? moves[move.ply + 1] : null;
    const covered = reply && reply.uci.slice(2, 4) === move.uci.slice(2, 4);
    const onBoard = move && game.history().length > move.ply;
    const visible = move && boardEl && !covered && onBoard && status !== "locked";

    if (!visible) {
        el.moveBadge.classList.add("hidden");
        el.repertoireBadge?.classList.add("hidden");
        return;
    }

    const size = boardEl.getBoundingClientRect().width / 8;
    const square = move.uci.slice(2, 4);
    const file = square.charCodeAt(0) - 97;
    const rank = Number(square[1]) - 1;
    const flipped = live.ground?.state?.orientation === "black";
    const col = flipped ? 7 - file : file;
    const row = flipped ? rank : 7 - rank;

    if (move.verdict.status === "rated") {
        el.moveBadge.style.left = `${(col + 1) * size}px`;
        el.moveBadge.style.top = `${row * size}px`;
        el.moveBadge.dataset.verdict = move.verdict.code;
        el.moveBadge.textContent = verdictGlyph(move.verdict.code);
        el.moveBadge.classList.remove("hidden");
    } else {
        el.moveBadge.classList.add("hidden");
    }

    const mark = repertoireMark(move.stats && move.stats.repertoire);
    if (el.repertoireBadge && mark) {
        // The opposite corner of the same square, so neither mark hides the other
        el.repertoireBadge.style.left = `${col * size}px`;
        el.repertoireBadge.style.top = `${(row + 1) * size}px`;
        el.repertoireBadge.dataset.repertoire = mark.state;
        el.repertoireBadge.textContent = mark.glyph;
        el.repertoireBadge.title = mark.title;
        el.repertoireBadge.classList.remove("hidden");
    } else {
        el.repertoireBadge?.classList.add("hidden");
    }
}

// ---------------------------------------------------------------------------------------------
// Progress: header HUD and panel
// ---------------------------------------------------------------------------------------------

// The progress badge is a component now: components/chrome/ProgressHud.tsx, in the shared header.

// The progress panel is a component now: components/progress/ProgressPanel.tsx, on /progress.

function announceNotice() {
    const notice = store.get('practice.notice');
    if (notice && store.get('practice.status') !== "offbook") {
        toast.showToast({ title: "Connect Lichess first", message: notice, type: "info", duration: 9000 });
    }
}

function announceImport() {
    const imported = store.get('progress.imported');
    if (imported > 0) {
        toast.showToast({
            title: "Progress saved", type: "success", duration: 6000,
            message: `${imported} practice${imported === 1 ? "" : "s"} from this device ${imported === 1 ? "is" : "are"} now in your account.`,
        });
    }
}

// ---------------------------------------------------------------------------------------------
// Line picker
// ---------------------------------------------------------------------------------------------

export async function renderLinePicker() {
    if (!el.linePickerCatalog) return;
    const summary = store.get('progress.summary');
    let catalogLines = [];
    try {
        catalogLines = await catalog.getLines();
    } catch (err) {
        el.linePickerCatalog.innerHTML = `<p class="line-picker__empty">The line catalog could not be loaded. Reload the page to try again.</p>`;
        return;
    }
    const column = (title, color) => {
        const ofColor = catalogLines.filter((l) => l.color === color);
        if (!ofColor.length) return "";
        return `
        <div class="line-picker__group">
            <h4>${title}</h4>
            <ul class="line-picker__list">
                ${ofColor.map((l) => lineItem(l, summary?.lines?.[l.slug], `catalog:${l.slug}`)).join("")}
            </ul>
        </div>`;
    };
    el.linePickerCatalog.innerHTML = column("You play White", "white") + column("You play Black", "black");

    const myLines = store.get('user.currentUser') ? lines.getActiveLines() : [];
    el.linePickerMyLines.classList.toggle("hidden", !store.get('user.currentUser'));
    el.linePickerMyLinesList.innerHTML = myLines.length
        ? myLines.map((l) => lineItem({ name: l.name, color: l.color, moves: "" }, summary?.lines?.[`line-${l.id}`], `user:${l.id}`)).join("")
        : `<li class="line-picker__empty">No lines of your own yet. Create one from any position.</li>`;

    pickerCatalogLines = catalogLines;
    pickerMyLines = myLines;
    renderSuggestedLines();

    // The catalog is the whole offer for a visitor, and clutter for someone whose own lines are right above
    const hasOwn = myLines.length > 0 || suggestedLines?.length > 0 || (suggestedLines === null && !!suggestedRequest);
    if (el.linePickerCatalogGroup && !catalogTouched) {
        catalogAutoState = !hasOwn;
        el.linePickerCatalogGroup.open = catalogAutoState;
    }
}

/** Numbered notation from a given ply on, so a variation reads "4...Nf6" and not the whole line again. */
function numberedFrom(sanMoves, fromPly = 0) {
    return sanMoves.slice(fromPly)
        .map((san, i) => {
            const ply = fromPly + i;
            if (ply % 2 === 0) return `${ply / 2 + 1}.${san}`;
            return i === 0 ? `${(ply - 1) / 2 + 1}...${san}` : san;
        })
        .join(" ");
}

/** "e2e4 c7c5" -> "1.e4 c5" */
/**
 * Lines taken from the user's own games. The request is not cheap (it reads the opening of every game of
 * the last year), and nothing on the home page shows it, so it is only asked for when the picker opens.
 */
export function loadSuggestedLines() {
    if (suggestedRequest || !store.get('user.currentUser')) return;
    suggestedRequest = api.fetchSuggestedLines()
        .catch((err) => {
            console.warn("[practice] Could not load suggested lines", err);
            return [];
        })
        .then((suggestions) => {
            suggestedLines = suggestions;
            // Through the whole picker: it is what loads the catalog the suggestions are named after
            renderLinePicker();
            return suggestions;
        });
}

/** A new import changes the answer, so the next time the picker opens it is asked for again. */
export function forgetSuggestedLines() {
    suggestedLines = null;
    suggestedRequest = null;
    expandedSuggestions.clear();
    shownSuggestions = SUGGESTIONS_SHOWN;
}

/** The suggestion at a path like "0" (a line) or "0.2" (its third variation). */
function suggestionAt(path) {
    return path.split(".").reduce((node, index) => {
        if (!node) return null;
        const list = node.variations || node;
        return list[Number(index)] || null;
    }, suggestedLines);
}

/**
 * Opens or closes a suggestion in place. Re-rendering the list would detach the button that was clicked,
 * and the handler that closes a dropdown on an outside click would no longer find it inside: the whole
 * picker closed on every expand.
 */
function toggleSuggestion(path) {
    const open = !expandedSuggestions.has(path);
    if (open) {
        expandedSuggestions.add(path);
    } else {
        expandedSuggestions.delete(path);
    }
    const button = el.linePickerSuggestedList?.querySelector(`[data-expand="${path}"]`);
    if (!button) return;
    button.setAttribute("aria-expanded", String(open));
    const chevron = button.querySelector(".line-picker__chevron");
    if (chevron?.textContent.trim()) chevron.textContent = open ? "\u25be" : "\u25b8";
    button.parentElement.querySelector(":scope > .line-picker__drill")?.classList.toggle("hidden", !open);
}

/**
 * The user's costliest opening positions, from their imported games. One that belongs to a catalog line is
 * practiced there (instant verdicts); any other can be saved as a line of their own. A suggestion that
 * splits into variations can be opened to practice the exact one that is costing the points.
 */
function renderSuggestedLines() {
    if (!el.linePickerSuggested) return;
    if (!store.get('user.currentUser')) {
        el.linePickerSuggested.classList.add("hidden");
        el.linePickerMoreBtn?.classList.add("hidden");
        return;
    }
    if (suggestedLines === null) {
        // Nothing to show until the picker is opened and the request goes out
        el.linePickerSuggested.classList.toggle("hidden", !suggestedRequest);
        el.linePickerSuggestedList.innerHTML = `<li class="line-picker__empty">Looking at your games...</li>`;
        el.linePickerMoreBtn?.classList.add("hidden");
        return;
    }
    // Worst first whatever the colour: the biggest leak is the one to practice, not the first White one
    const ordered = [...suggestedLines].sort((a, b) => b.pointsLost - a.pointsLost);
    const shown = ordered.slice(0, shownSuggestions);
    el.linePickerSuggested.classList.toggle("hidden", !ordered.length);
    el.linePickerSuggestedList.innerHTML = shown.map((s) => suggestionItem(s, String(suggestedLines.indexOf(s)), 0, null)).join("");
    el.linePickerMoreBtn?.classList.toggle("hidden", ordered.length <= shown.length);
    if (el.linePickerMoreBtn) el.linePickerMoreBtn.textContent = `Show ${ordered.length - shown.length} more`;
}

/** A white or a black pawn, so the side reads at a glance and not only in the "As White" text. */
function pawnIcon(color) {
    return `<svg class="line-picker__pawn" data-side="${color}" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2.5a3.6 3.6 0 0 0-2 6.6c-1 .7-1.7 1.9-1.7 3.2 0 1.5.8 2.8 2 3.6-.5 1.7-1.5 3.2-2.9 4.3-.6.5-.3 1.3.4 1.3h8.4c.7 0 1-.8.4-1.3-1.4-1.1-2.4-2.6-2.9-4.3 1.2-.8 2-2.1 2-3.6 0-1.3-.7-2.5-1.7-3.2a3.6 3.6 0 0 0-2-6.6z"/>
    </svg>`;
}

function suggestionItem(suggestion, path, parentPlies, parentName) {
    const san = sanFromUci(suggestion.uciMoves);
    const related = suggested.relatedCatalogLine(pickerCatalogLines, suggestion);
    const owned = pickerMyLines.find((l) => (l.uciMoves || "").trim() === suggestion.uciMoves && l.color === suggestion.color);
    const isVariation = parentPlies > 0;
    const name = suggestion.name || null;
    const title = isVariation ? numberedFrom(san, parentPlies) : (name || numberedFrom(san));
    const side = suggestion.color === "white" ? "White" : "Black";
    const record = `${suggestion.games} games \u00b7 ${suggestion.wins}W ${suggestion.draws}D ${suggestion.losses}L`;
    // The name earns its place only when the title is not already it, nor the one the parent showed;
    // the moves, whenever the title is a name or the tail of a variation instead of the moves themselves
    const movesLabel = numberedFrom(san);
    const extra = [name && name !== title && name !== parentName ? name : "", title === movesLabel ? "" : movesLabel]
        .filter(Boolean).join(" \u00b7 ");
    const detail = isVariation ? record : `${pawnIcon(suggestion.color)} As ${side} \u00b7 ${record}`;
    const variations = suggestion.variations || [];
    const open = expandedSuggestions.has(path);
    const chevron = variations.length
        ? `<span class="line-picker__chevron" aria-hidden="true">${open ? "\u25be" : "\u25b8"}</span>`
        : `<span class="line-picker__chevron"></span>`;

    const actions = [];
    if (related) {
        actions.push(`<button type="button" class="btn btn-ghost btn-sm" data-line="catalog:${escapeHtml(related.slug)}">Practice ${escapeHtml(related.name)}</button>`);
    }
    if (owned) {
        actions.push(`<button type="button" class="btn btn-ghost btn-sm" data-line="user:${owned.id}">Practice my line</button>`);
    } else {
        actions.push(`<button type="button" class="btn btn-ghost btn-sm" data-create="${escapeHtml(path)}">Create a line like this</button>`);
    }
    const rest = suggestion.games - suggestion.coveredGames;

    return `<li class="line-picker__branch">
        <button type="button" class="line-picker__item line-picker__item--tree" data-expand="${escapeHtml(path)}"
                aria-expanded="${open}">
            <span class="line-picker__name">${escapeHtml(title)}</span>
            <span class="line-picker__moves">${detail}${extra ? ` \u00b7 ${escapeHtml(extra)}` : ""}</span>
            <span class="line-picker__record">
                <span class="line-picker__best" data-score="${suggestion.pointsLost > 0 ? "low" : "ok"}">you take ${pointsLong(suggestion.scorePct)}</span>
            </span>
            ${chevron}
        </button>
        <div class="line-picker__drill${open ? "" : " hidden"}">
            <div class="line-picker__actions">${actions.join("")}</div>
            ${variations.length ? `<p class="line-picker__note">${suggestion.pointsLost > 0 ? "Where those points go:" : "What it splits into:"}</p>
            <ul class="line-picker__list line-picker__sublist">
                ${variations.map((v, i) => suggestionItem(v, `${path}.${i}`, san.length, name)).join("")}
            </ul>
            ${rest > 0 ? `<p class="line-picker__note line-picker__rest">${rest} more game${rest === 1 ? "" : "s"} spread over lines played too rarely to list.</p>` : ""}` : ""}
        </div>
    </li>`;
}

function lineItem(line, record, key) {
    const best = record && record.bestAccuracy !== null
        ? `<span class="line-picker__stars">${stars(record.stars)}</span><span class="line-picker__best">${Math.round(record.bestAccuracy)}%</span>`
        : `<span class="line-picker__best line-picker__best--new">${escapeHtml(LEVEL_LABELS[line.level] || "")}</span>`;
    return `<li><button type="button" class="line-picker__item" data-line="${escapeHtml(key)}">
        <span class="line-picker__name">${escapeHtml(line.name)}</span>
        <span class="line-picker__moves">${escapeHtml(line.moves ? catalog.numberedMoves(line.moves) : "")}</span>
        <span class="line-picker__record">${best}</span></button></li>`;
}

async function chooseLine(key) {
    const catalogLines = pickerCatalogLines;
    const myLines = pickerMyLines;
    const [source, id] = key.split(":");
    if (source === "catalog") {
        const line = catalogLines.find((l) => l.slug === id);
        if (line) practice.startLine({ ...line, source: "catalog" });
        return;
    }
    const line = myLines.find((l) => String(l.id) === id);
    if (!line) return;
    practice.startLine({ source: "user", id: line.id, name: line.name, color: line.color || "white", fen: line.fen, uci: line.uciMoves });
}

// ---------------------------------------------------------------------------------------------
// Login wall
// ---------------------------------------------------------------------------------------------

export function openLoginWall() {
    if (!el.loginWall) return;
    const summary = store.get('progress.summary');
    const done = summary ? summary.completedCount : 0;
    // Nothing to celebrate before the first practice, and "0 practices done" is a poor way to ask
    el.loginWallLead.textContent = done ? `${done} practice${done === 1 ? "" : "s"} done` : "";
    el.loginWallLead.classList.toggle("hidden", !done);
    const keeps = [];
    if (summary) {
        // Before the first practice there is nothing earned to keep, and saying "0 XP" only undersells it
        if (summary.totalXp) keeps.push(`${summary.totalXp} XP and your ${summary.belt.name}`);
        if (summary.totalStars) keeps.push(`${summary.totalStars} star${summary.totalStars === 1 ? "" : "s"} on ${Object.keys(summary.lines).length} line${Object.keys(summary.lines).length === 1 ? "" : "s"}`);
        if (summary.streak.current) keeps.push(`Your ${summary.streak.current}-day streak`);
    }
    el.loginWallKeeps.innerHTML = keeps.map((k) => `<li>${escapeHtml(k)}</li>`).join("");
    focusBeforeWall = document.activeElement;
    el.loginWall.classList.remove("hidden");
    document.body.classList.add("modal-open");
    document.getElementById("loginWallGoogle")?.focus();
    track.event("wall_opened", { practice: done });
}

function closeLoginWall() {
    el.loginWall?.classList.add("hidden");
    document.body.classList.remove("modal-open");
    focusBeforeWall?.focus?.();
    focusBeforeWall = null;
}

/** Keeps Tab inside the dialog while it is open. */
function trapFocusInWall(e) {
    if (e.key !== "Tab" || !el.loginWall || el.loginWall.classList.contains("hidden")) return;
    const focusable = [...el.loginWall.querySelectorAll("a[href], button:not([disabled])")];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
    }
}
